import React, { useState } from 'react';
import { Card, Badge, ProgressBar, Button } from './Shared';
import SafeIcon from '../common/SafeIcon';
import { shipments, routeTimeline, customsChecklist } from '../data/mockData';
import { motion, AnimatePresence } from 'framer-motion';

const ShipmentDetailsPanel = ({ shipment, onClose }) => {
  return (
    <motion.div 
      initial={{ x: '100%', opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: '100%', opacity: 0 }}
      transition={{ type: 'spring', damping: 25, stiffness: 200 }}
      className="fixed inset-y-0 right-0 w-full md:w-[500px] bg-navy-800 border-l border-navy-600 shadow-2xl z-50 flex flex-col"
    >
      <div className="p-5 border-b border-navy-700 flex justify-between items-center bg-navy-900/50">
        <div>
          <h2 className="text-lg font-bold text-slate-100 font-mono">{shipment.id}</h2>
          <p className="text-sm text-slate-400">{shipment.origin} <SafeIcon name="ArrowRight" className="inline mx-1" /> {shipment.destination}</p>
        </div>
        <button onClick={onClose} className="p-2 hover:bg-navy-700 rounded-full text-slate-400 transition-colors">
          <SafeIcon name="X" />
        </button>
      </div>

      <div className="flex-grow overflow-y-auto p-5 space-y-6 custom-scrollbar">
        {/* Status Overview */}
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-navy-900 p-3 rounded-lg border border-navy-700">
            <p className="text-xs text-slate-500 mb-1">Carrier Details</p>
            <p className="font-medium text-slate-200">{shipment.carrier}</p>
            <p className="text-xs text-slate-400 mt-1">{shipment.container}</p>
          </div>
          <div className="bg-navy-900 p-3 rounded-lg border border-navy-700">
            <p className="text-xs text-slate-500 mb-1">ETA</p>
            <p className="font-medium text-cyan-400">{new Date(shipment.eta).toLocaleDateString()}</p>
            <p className="text-xs text-slate-400 mt-1">{new Date(shipment.eta).toLocaleTimeString()}</p>
          </div>
        </div>

        {/* Route Timeline */}
        <div>
          <h3 className="text-sm font-semibold text-slate-300 mb-4 flex items-center gap-2">
            <SafeIcon name="Map" /> Route Timeline
          </h3>
          <div className="relative border-l-2 border-navy-700 ml-3 space-y-6">
            {routeTimeline.map((step, i) => (
              <div key={i} className="relative pl-6">
                <span className={`absolute -left-[9px] top-1 h-4 w-4 rounded-full border-2 border-navy-800 ${
                  step.status === 'completed' ? 'bg-green-500 glow-green' : 
                  step.status === 'current' ? 'bg-orange-500 glow-orange animate-pulse' : 'bg-navy-600'
                }`}></span>
                <div className="flex justify-between items-start">
                  <div>
                    <p className={`text-sm font-medium ${step.status === 'pending' ? 'text-slate-500' : 'text-slate-200'}`}>{step.step}</p>
                    <p className="text-xs text-slate-400">{step.location}</p>
                  </div>
                  <span className="text-xs font-mono text-slate-500">{step.date}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Customs Checklist */}
        <div>
          <h3 className="text-sm font-semibold text-slate-300 mb-4 flex items-center gap-2">
            <SafeIcon name="FileText" /> Customs & Docs Checklist
          </h3>
          <div className="space-y-2">
            {customsChecklist.map((item, i) => (
              <div key={i} className="flex items-center justify-between p-3 bg-navy-900 border border-navy-700 rounded-lg">
                <div className="flex items-center gap-3">
                  {item.status === 'Verified' ? (
                    <SafeIcon name="CheckCircle" className="text-green-500" />
                  ) : item.status === 'Missing' ? (
                    <SafeIcon name="AlertCircle" className="text-alert-500" />
                  ) : (
                    <SafeIcon name="Clock" className="text-orange-500" />
                  )}
                  <span className="text-sm text-slate-300">{item.doc} {item.required && <span className="text-alert-500">*</span>}</span>
                </div>
                <Badge type={item.status === 'Verified' ? 'success' : item.status === 'Missing' ? 'danger' : 'warning'}>
                  {item.status}
                </Badge>
              </div>
            ))}
          </div>
        </div>
        
        {/* Customer Updates Log */}
        <div>
          <h3 className="text-sm font-semibold text-slate-300 mb-4 flex items-center gap-2">
            <SafeIcon name="MessageSquare" /> Customer Updates
          </h3>
          <div className="bg-navy-900 border border-navy-700 rounded-lg p-4 space-y-3">
             <div className="text-xs font-mono text-slate-500 mb-1">System Log - Oct 17, 22:15</div>
             <p className="text-sm text-slate-300">Automated notification sent to customer: "Vessel has departed origin port."</p>
             <div className="border-t border-navy-700 my-2"></div>
             <div className="text-xs font-mono text-slate-500 mb-1">Operator: Jane Doe - Oct 16, 09:00</div>
             <p className="text-sm text-slate-300">Verified packing list matches commercial invoice. Awaiting final BL release.</p>
          </div>
        </div>
      </div>

      <div className="p-5 border-t border-navy-700 bg-navy-900/50 flex gap-3">
        <Button variant="primary" className="flex-1" icon="Share2">Share Tracking</Button>
        <Button variant="secondary" className="flex-1" icon="Download">Download Docs</Button>
      </div>
    </motion.div>
  );
};

const ShipmentBoard = () => {
  const [selectedShipment, setSelectedShipment] = useState(null);
  const [filter, setFilter] = useState('All');

  const getStatusType = (status) => {
    switch(status) {
      case 'Delivered': return 'success';
      case 'Exception': 
      case 'Customs Hold': return 'danger';
      case 'In Transit': return 'info';
      default: return 'warning';
    }
  };

  const getTypeIcon = (type) => {
    if (type.includes('Sea')) return 'Anchor';
    if (type.includes('Air')) return 'Navigation';
    return 'Truck';
  };

  return (
    <div className="space-y-6 relative h-full">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-100">Shipment Board</h1>
          <p className="text-slate-400 mt-1">Manage and track active freight.</p>
        </div>
        <div className="flex gap-2 bg-navy-900 p-1 rounded-lg border border-navy-700">
          {['All', 'In Transit', 'Exceptions', 'Delivered'].map(f => (
            <button 
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-1.5 text-sm rounded-md transition-colors ${filter === f ? 'bg-navy-700 text-cyan-400 font-medium shadow-sm' : 'text-slate-400 hover:text-slate-200'}`}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      <Card noPadding className="overflow-hidden">
        <div className="overflow-x-auto custom-scrollbar">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-navy-900 border-b border-navy-700 text-xs uppercase tracking-wider text-slate-500">
                <th className="p-4 font-medium">Tracking ID</th>
                <th className="p-4 font-medium">Origin <SafeIcon name="ArrowRight" className="inline mx-1"/> Destination</th>
                <th className="p-4 font-medium">Carrier</th>
                <th className="p-4 font-medium">Status & Progress</th>
                <th className="p-4 font-medium text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-navy-700">
              {shipments.map((shipment) => (
                <tr 
                  key={shipment.id} 
                  className="hover:bg-navy-800/50 transition-colors cursor-pointer group"
                  onClick={() => setSelectedShipment(shipment)}
                >
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded bg-navy-900 border border-navy-700 text-slate-400 group-hover:text-cyan-400 group-hover:border-cyan-500/30 transition-colors">
                        <SafeIcon name={getTypeIcon(shipment.type)} />
                      </div>
                      <div>
                        <p className="font-mono text-sm font-medium text-slate-200 group-hover:text-cyan-400 transition-colors">{shipment.id}</p>
                        <p className="text-xs text-slate-500">{shipment.type}</p>
                      </div>
                    </div>
                  </td>
                  <td className="p-4">
                    <div className="text-sm">
                      <p className="text-slate-300 truncate max-w-[200px]">{shipment.origin.split(',')[0]}</p>
                      <p className="text-slate-500 text-xs mt-0.5 truncate max-w-[200px]">{shipment.destination.split(',')[0]}</p>
                    </div>
                  </td>
                  <td className="p-4">
                    <span className="text-sm text-slate-300">{shipment.carrier}</span>
                  </td>
                  <td className="p-4 min-w-[200px]">
                    <div className="flex justify-between items-center mb-2">
                       <Badge type={getStatusType(shipment.status)}>{shipment.status}</Badge>
                       <span className="text-xs text-slate-500 font-mono">{shipment.progress}%</span>
                    </div>
                    <ProgressBar 
                      progress={shipment.progress} 
                      colorClass={shipment.status === 'Exception' || shipment.status === 'Customs Hold' ? 'bg-alert-500' : 'bg-cyan-500'} 
                    />
                  </td>
                  <td className="p-4 text-right">
                    <Button variant="outline" className="py-1.5 px-3 text-xs opacity-0 group-hover:opacity-100 transition-opacity">
                      View Details
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Side Panel Overlay */}
      <AnimatePresence>
        {selectedShipment && (
          <>
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelectedShipment(null)}
              className="fixed inset-0 bg-navy-900/60 backdrop-blur-sm z-40"
            />
            <ShipmentDetailsPanel shipment={selectedShipment} onClose={() => setSelectedShipment(null)} />
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ShipmentBoard;